import React, { useEffect, useState } from "react"
import { Modal, Form, Input, Button, Typography, Flex, Radio } from "antd"
import LabeledField from "pagesV2/client/Profile/Tabs/ProfileInfo/LabeledField"
import { useTranslation } from "react-i18next"
import PropTypes from "prop-types"
const { Title } = Typography

const FEE_TYPE = { FIXED: "fixed", PERCENTAGE: "percentage" }

const AddNewFeeModal = ({ visible, onCancel, onAdd, initialValues, isSaving }) => {
  const [form] = Form.useForm()
  const { t } = useTranslation()
  const titleValue = Form.useWatch("title", form)
  const priceValue = Form.useWatch("price", form)
  const feeTypeValue = Form.useWatch("fee_type", form)

  const [hasErrors, setHasErrors] = useState(false)

  const isUnchanged = initialValues
    ? titleValue === initialValues.title &&
      String(priceValue ?? "") === String(initialValues.price ?? "") &&
      feeTypeValue === initialValues.fee_type
    : !titleValue || !priceValue

  useEffect(() => {
    if (visible && initialValues) {
      form.setFieldsValue({
        title: initialValues.title,
        price: initialValues.price,
        fee_type: initialValues.fee_type ?? FEE_TYPE.FIXED
      })
    } else if (visible && !initialValues) {
      form.resetFields()
      form.setFieldsValue({ fee_type: FEE_TYPE.FIXED })
    }
  }, [visible, initialValues])

  const handleCancel = () => {
    form.resetFields()
    onCancel()
  }

  const priceLabel =
    feeTypeValue === FEE_TYPE.PERCENTAGE ? "settings.tax_price_title_percentage" : "settings.tax_price_title"

  return (
    <Modal open={visible} onCancel={handleCancel} footer={null} width={500} maskClosable={false} centered destroyOnClose>
      <Title level={4} className="mb-m">
        {initialValues ? t("settings.edit_custom_fee") : t("settings.add_new_fee")}
      </Title>
      <Form
        form={form}
        layout="vertical"
        onFinish={onAdd}
        onFieldsChange={(_, allFields) => setHasErrors(allFields.some((f) => f.errors?.length > 0))}
      >
        <LabeledField
          labelKey="settings.tax_fee_title"
          name="title"
          rules={[{ required: true, message: t("settings.tax_fee_title_required") }]}
        >
          <Input placeholder="e.g., Service Tax, Booking Fee" size="large" />
        </LabeledField>
        <LabeledField labelKey="settings.fee_type_label" name="fee_type">
          <Radio.Group>
            <Radio value={FEE_TYPE.FIXED}>{t("settings.fee_type_fixed")}</Radio>
            <Radio value={FEE_TYPE.PERCENTAGE}>{t("settings.fee_type_percentage")}</Radio>
          </Radio.Group>
        </LabeledField>
        <Form.Item
          name="price"
          dependencies={["fee_type"]}
          rules={[
            {
              validator: (_, value) => {
                if (value === undefined || value === null || value === "")
                  return Promise.reject(new Error(t("settings.tax_price_title_required")))
                const numValue = Number(value)
                if (numValue <= 0) return Promise.reject(new Error(t("settings.tax_price_greater_than_zero")))
                const feeType = form.getFieldValue("fee_type")
                if (feeType === FEE_TYPE.PERCENTAGE && numValue > 100) {
                  return Promise.reject(new Error(t("settings.tax_price_max_percentage")))
                }
                const decimalMatch = String(value).match(/\.(\d+)/)
                if (decimalMatch && decimalMatch[1].length > 2) {
                  return Promise.reject(new Error(t("settings.tax_price_max_two_decimals")))
                }
                return Promise.resolve()
              }
            }
          ]}
          label={t(priceLabel)}
        >
          <Input type="number" size="large" placeholder="0.00" min={0} step="0.01" style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item>
          <Flex justify="end" gap={8} className="mt-s">
            <Button onClick={handleCancel} color="primary" variant="outlined" disabled={isSaving}>
              {t("common.cancel")}
            </Button>
            <Button type="primary" htmlType="submit" color="primary" variant="solid" loading={isSaving} disabled={isUnchanged || hasErrors}>
              {initialValues ? t("common.save") : t("settings.add_fee")}
            </Button>
          </Flex>
        </Form.Item>
      </Form>
    </Modal>
  )
}

AddNewFeeModal.propTypes = {
  visible: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  initialValues: PropTypes.shape({
    title: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    fee_type: PropTypes.string
  }),
  isSaving: PropTypes.bool
}

export default AddNewFeeModal
